import { Box, Flex, Group, Text } from '@mantine/core';
import { ReactNode, useState } from 'react';
import { ClientListItem } from 'src/admin/users/types';
import { Button as DesignButton } from 'src/design-system/Button';
import { CollapsibleSearch } from 'src/design-system/CollapsibleSearch';
import { FlowNavigationFooter } from 'src/design-system/FlowNavigationFooter';
import { FlowBody } from 'src/UI/generate-flow/FlowBody';
import { FlowLayout } from 'src/UI/generate-flow/FlowLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import CreateClientModal from '../CreateClientModalV2';
import MemberSearchList from './MemberSearchList';

type Props = {
  title: ReactNode;
  selectedMembers: ClientListItem[];
  onToggle: (client: ClientListItem) => void;
  onCancel: () => void;
  onBack: () => void;
  onContinue: () => void;
  continueDisabled: boolean;
  continueDisabledTooltip?: string;
};

export default function SelectMembersStep({
  title,
  selectedMembers,
  onToggle,
  onCancel,
  onBack,
  onContinue,
  continueDisabled,
  continueDisabledTooltip,
}: Props) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddIndividual, setShowAddIndividual] = useState(false);

  const handleClientCreated = (client: ClientListItem) => {
    onToggle(client);
    setShowAddIndividual(false);
  };

  return (
    <>
      <Flex direction="column" h="100%">
        <FlowLayout
          header={
            <Box px="xl" pt="xxl" pb="sm">
              <Group justify="space-between" pb="lg">
                <Text c="gray.10" size="displaySm" fw={FONT_WEIGHTS.semibold}>
                  {title}
                </Text>
                <DesignButton variant="text" onClick={onCancel}>
                  Cancel
                </DesignButton>
              </Group>

              <Group pb="md" gap="sm">
                <CollapsibleSearch
                  open={searchOpen}
                  onOpenChange={setSearchOpen}
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Search by name, email, address"
                >
                  <DesignButton variant="secondary" onClick={() => setShowAddIndividual(true)}>
                    Add new individual
                  </DesignButton>
                </CollapsibleSearch>
              </Group>
            </Box>
          }
          body={
            <FlowBody
              footerAlign="center"
              footer={
                <FlowNavigationFooter
                  onBack={onBack}
                  onAction={onContinue}
                  actionDisabled={continueDisabled}
                  actionDisabledTooltip={continueDisabledTooltip}
                />
              }
            >
              <Box px="xs">
                <MemberSearchList
                  selectedMembers={selectedMembers}
                  onToggle={onToggle}
                  searchTerm={searchTerm}
                  noScroll
                  excludeInFamilies
                />
              </Box>
            </FlowBody>
          }
        />
      </Flex>
      <CreateClientModal
        opened={showAddIndividual}
        close={() => setShowAddIndividual(false)}
        navigateAfterCreation={false}
        onClientCreated={handleClientCreated}
      />
    </>
  );
}
